import React, { useState, useEffect } from 'react'
import qs from 'qs'
import { useLocation } from 'react-router-dom'
import { useCmsBlock, useCart } from 'lib/core/hooks'
import { PRODUCT_TYPENAME } from 'lib/core/resources/constants'
import { Button, FlexWrapper, Spinner } from 'lib/ui/components'
import { useStoreView } from 'hooks/useStoreView'
import { useFreeShippingData, useSubscriptionData } from 'hooks/subscriptions'
import { CMS_BLOCK } from 'app/sofia/resources/constants'
import { CMS_BLOCK as CMS_LIVBRITE } from 'app/livbrite/resources/constants'
import { useListrakProductBrowse } from 'modules/listrak/hooks/useListrakProductBrowse'
import { ModalGlobalStyle } from 'components/Modal'
import { GTM } from 'lib/core/services/gtm'
import { ConfigurableVariant, ProductData } from 'lib/types'
import { ProductNotification, ConfigurableOptions, ProductPrice } from 'lib/modules/catalog'
import { SWATCHES_ATTRIBUTES } from 'resources/constants'
import { Swatches } from '../Swatches'
import {
  Wrapper,
  ButtonWrapper,
  ModalExtraWrapper,
  PriceWrapper
} from './styled'
import { SizingChart } from './components/SizingChart'
import { Subscriptions } from './components/Subscriptions'
import { OneTime } from './components/OneTime'
import { Ga4Events } from '../../../../lib/gtm/index'

interface CmsBlockData {
  id: string
  updatedAt?: number
  content: string
  isSuccess: boolean
  isFailure: boolean
  isFirstLoad: boolean
}

interface Props {
  sizingChart?: string
  customAttributes: any
  fromProductSelector: boolean
  productImage: string
  forceOneTimePurchase?: boolean
  setProductImage: Function
  product: ProductData
  [key: string]: any | undefined
}

interface CartItem {
  quantity: number
  sku: string
  frequency_in_cart: string | null
  survey_referral?: number
}

interface ConfigurableCartItem {
  quantity: number
  sku: string
  frequency_in_cart: string | null
  survey_referral?: number
  configurableSku: string
}

export interface QuantityValue {
  value: number
}

const swatchesAttributes = Object.values(SWATCHES_ATTRIBUTES)

export const OptionsSection = ({
  sizingChart,
  customAttributes,
  fromProductSelector,
  productImage,
  forceOneTimePurchase,
  setProductImage,
  product
}: Props) => {
  const {
    id: productId,
    name: productName,
    sku,
    price,
    typeName: priceType,
    variants
  } = product

  const location = useLocation()
  const queryString = qs.parse(location.search.substring(1))
  const { isSofiaAndGrace } = useStoreView()
  const [loading, setLoading] = useState(false)
  const sizingChartBottomBlockId = isSofiaAndGrace ? CMS_BLOCK.SIZE_CHART_BOTTOM : CMS_LIVBRITE.SIZE_CHART_BOTTOM
  const sizeChartBottomBlock = useCmsBlock({
    id: sizingChartBottomBlockId,
    isLazy: false

  }) as CmsBlockData
  const [selectedTab, setSelectedTab] = useState<number | undefined>(undefined)
  const [isModalOpen, setIsModalOpen] = useState(false)
  const [variant, setVariant] = useState(variants[0])
  const [frequency, setFrequency] = useState(0)
  const [quantityPerDay, setQuantityPerDay] = useState<QuantityValue>({ value: 2 })
  const frequencyInDays = frequency * 7
  const { cartId, addSimpleProduct, addConfigurableProduct } = useCart()
  const [renderPrice, setRenderPrice] = useState(price)
  const [renderPriceType, setRenderPriceType] = useState(priceType)
  const isSimpleProduct = priceType === PRODUCT_TYPENAME.SIMPLE
  const isConfigurableProduct = priceType === PRODUCT_TYPENAME.CONFIGURABLE
  const defaultPackageQty = (customAttributes?.package_qty && customAttributes?.package_qty[0]) ?? (variants?.length ? variants[0].product?.customAttribute?.package_qty : 1)
  const availableFrequencies = isSimpleProduct ? customAttributes.available_frequencies : variant?.product?.customAttribute?.available_frequencies
  const [packageQty, setPackageQty] = useState(defaultPackageQty)
  const [packsToAdd, setPacksToAdd] = useState(0)
  const [subscriptionsEligible] = customAttributes?.subscription_eligible ?? []
  const isSubscriptionEligible = subscriptionsEligible !== 'No'
  const hasSubscriptions = isSubscriptionEligible && availableFrequencies
  const [qty, setQty] = useState(hasSubscriptions ? 0 : 1)
  const variantId = isConfigurableProduct ? variant?.product?.id : productId
  const [packageType] = customAttributes?.package ?? ['pack']
  const qtyToAdd = packsToAdd > 0 ? packsToAdd : qty

  useEffect(() => {
    setProductImage(variant?.product.image?.default?.url)
  }, [variant, setProductImage])

  useListrakProductBrowse({ sku: variant?.product.sku ?? getDefaultVariantSku(variants) ?? sku })

  const optionFromPath = queryString.option?.toString()
  const getVariantFromOptionValue = (option?: string) => {
    return variants.find((variant: ConfigurableVariant) => {
      return variant.attributes[0]?.value_index.toString() === option
    })
  }
  const skuFromPath = queryString.sku?.toString() ?? getVariantFromOptionValue(optionFromPath)?.product.sku

  function handleQtyChange (qty: any) {
    const quantity = parseInt(qty.target.value)
    if (quantity > 0 && quantity < 9999) {
      setQty(quantity)
    }
  }

  function handlePriceChange (price: any) {
    setRenderPrice(price)
    setRenderPriceType(PRODUCT_TYPENAME.SIMPLE)
  }

  function handleConfigurableChange (event: any) {
    const { product } = event
    const [piecesPerSelectedPackage] = product.customAttribute?.package_qty ?? []
    setVariant({ attributes: product.customAttributeList, product: product })
    setPackageQty(piecesPerSelectedPackage)
  }

  function handleAddConfigurableToCart () {
    setLoading(true)
    const configurableSku = sku

    const cartItems: ConfigurableCartItem[] = [{
      configurableSku,
      quantity: qtyToAdd,
      sku: variant ? variant?.product.sku : '',
      frequency_in_cart: hasSubscriptions ? frequencyInDays.toString() : null
    }]

    if (fromProductSelector) {
      cartItems[0].survey_referral = 1
    }

    addConfigurableProduct({
      configurableSku,
      cartItems,
      options: {
        callback: {
          onFailure: () => setLoading(false),
          onSuccess: () => setLoading(false)
        }
      }
    })
  }

  function handleAddSimpleToCart () {
    setLoading(true)
    const cartItems: CartItem[] = [{
      quantity: qtyToAdd,
      sku: sku,
      frequency_in_cart: hasSubscriptions ? frequencyInDays.toString() : null
    }]

    if (fromProductSelector) {
      cartItems[0].survey_referral = 1
    }

    addSimpleProduct({
      sku,
      cartItems,
      options: {
        callback: {
          onFailure: () => setLoading(false),
          onSuccess: () => setLoading(false)
        }
      }
    })
  }
  const { subscriptionsData } = useSubscriptionData(availableFrequencies, renderPrice)
  const [variantSize] = variant?.product.customAttribute?.size ?? []
  const showOnTimePurchase = selectedTab === 1 || forceOneTimePurchase
  const isValidToAddToCart = hasSubscriptions ? (qtyToAdd > 0) : (qty > 0)

  const { itemDiscount, loadingSubscription, isFreeShipping } = useFreeShippingData(variantId, hasSubscriptions ? (frequency * 7) : 0, qtyToAdd)
  const priceValue = renderPrice.regularPrice?.amount.value
  const subTotalCalc = itemDiscount ? (priceValue * qtyToAdd) - itemDiscount : priceValue * qtyToAdd
  const subtotalPrice = parseFloat(subTotalCalc.toFixed(2))

  useEffect(() => {
    if (!sku) {
      return
    }

    GTM.productDetail({
      product
    })
    Ga4Events.viewItem({ product })
  }, [sku, product, isConfigurableProduct])

  const handleOpenSubscriptions = () => {
    setSelectedTab(0)
    setFrequency(4)
    setPacksToAdd(1)
  }

  const handleOpenOneTime = () => {
    setSelectedTab(1)
    setFrequency(0)
    setPacksToAdd(0)
    setQty(1)
  }

  return (
    <Wrapper isSofiaAndGrace={isSofiaAndGrace}>
      <ProductNotification
        showToasts
        showModal
        sku={sku}
        productName={productName}
        productImage={productImage}
        continueShoppingLink='/bladder-control'
        modalExtraContent={
          <ModalExtraWrapper>
            {hasSubscriptions && frequencyInDays > 0 && (
              <div>
                <strong>Subscription</strong>
                <p>Ships every {frequency} weeks</p>
              </div>
            )}
            {isConfigurableProduct && variantSize && (
              <div>
                <strong>Size: </strong>
                <span>{variantSize}</span>
              </div>
            )}
          </ModalExtraWrapper>
        }
      />

      <PriceWrapper>
        {(renderPrice.regularPrice.amount.value > 0) && (
          <>
            <ProductPrice
              price={renderPrice}
              type={renderPriceType}
            />
            <p>per bag / {packageQty} total pieces</p>
          </>
        )}
      </PriceWrapper>

      <FlexWrapper>
        <ConfigurableOptions
          sku={sku}
          basePrice={renderPrice}
          onPriceChange={handlePriceChange}
          onChange={handleConfigurableChange}
          renderOptions={Swatches}
          swatchesAttributes={swatchesAttributes}
          defaultVariant={skuFromPath ?? variant?.product?.sku}
          reloadOptions
        />
        {sizingChart && (
          <SizingChart
            setIsModalOpen={setIsModalOpen}
            isModalOpen={isModalOpen}
            sizingChart={sizingChart}
            sizeChartBottomBlock={sizeChartBottomBlock}
            isSofia={isSofiaAndGrace}
          />
        )}
      </FlexWrapper>

      <div className='product__actions'>
        {hasSubscriptions ? (
          <>
            <h3 className='product__steps'>2. Select frequency</h3>
            <div className='product__frequency-container'>
              <label className={`product__frequency-option ${selectedTab === 0 && 'active'}`}>
                <input
                  checked={Boolean(selectedTab === 0 && !showOnTimePurchase)}
                  name='subscription'
                  type='radio'
                  onChange={handleOpenSubscriptions}
                />
                <strong>
                  Schedule and Save 10% on Every Order- Plus a Free Gift!
                </strong>
              </label>
              {Boolean(selectedTab === 0 && !showOnTimePurchase) && (
                <Subscriptions
                  productId={variantId}
                  packageQty={packageQty}
                  frequency={frequency}
                  setFrequency={setFrequency}
                  productPrice={renderPrice}
                  packsToAdd={packsToAdd}
                  setPacksToAdd={setPacksToAdd}
                  subscriptionsData={subscriptionsData}
                  quantityPerDay={quantityPerDay}
                  setQuantityPerDay={setQuantityPerDay}
                  packageType={packageType}
                  subtotalPrice={subtotalPrice}
                />
              )}
              <label className={`product__frequency-option ${selectedTab === 1 && 'active'}`}>
                <input
                  checked={showOnTimePurchase}
                  name='one_time'
                  type='radio'
                  onChange={handleOpenOneTime}
                />
                <strong>
                  One Time Purchase
                </strong>
              </label>
              {showOnTimePurchase && (
                <OneTime
                  handleQtyChange={handleQtyChange}
                  qty={qty}
                  packageType={packageType}
                  packageQty={packageQty}
                  frequency={frequency}
                  productId={variantId}
                  price={renderPrice}
                  subtotalPrice={subtotalPrice}
                  onReturnToSubscriptions={handleOpenSubscriptions}
                  showReturnToSubscription
                />
              )}
            </div>
          </>
        ) : (
          <OneTime
            handleQtyChange={handleQtyChange}
            qty={qty}
            packageType={packageType}
            packageQty={packageQty}
            frequency={frequency}
            productId={variantId}
            price={renderPrice}
            subtotalPrice={subtotalPrice}
          />
        )}
      </div>

      <ButtonWrapper
        isSofia={isSofiaAndGrace}
      >
        <div className='product__subtotal'>
          {subtotalPrice > 0 && (
            <>
              <label>Subtotal</label>
              <p>
                {loadingSubscription ? (
                  <><Spinner size={16} /></>
                ) : (
                  <>
                    $ {subtotalPrice}
                    {isFreeShipping && (
                      <span>Free Shipping</span>
                    )}
                  </>
                )}
              </p>
            </>
          )}
        </div>
        <Button
          isLoading={loading}
          isDisabled={!isValidToAddToCart || (isSimpleProduct ? !cartId : !variant || !cartId)}
          onClick={isSimpleProduct ? handleAddSimpleToCart : handleAddConfigurableToCart}
        >
          Add To Cart
        </Button>
      </ButtonWrapper>
      <ModalGlobalStyle />
    </Wrapper>
  )
}

export const getPackQty = (packageQty: number, qtyPerDay: number, itemFrequency: number) => {
  const daysInWeek = 7
  return Math.ceil((itemFrequency * daysInWeek * qtyPerDay) / packageQty)
}

const getDefaultVariantSku = (variants?: any[]) => {
  const defaultVariant = variants?.find(variant => variant.product.isDefault > 0)
  return defaultVariant?.sku ?? variants?.[0]?.product.sku
}
